import React from 'react';
import { PhotoEntity } from '@interpals/api-web';
import clsx from 'clsx';

import Image from 'components/Image';
import Icon from 'components/Icon/Icon';
import { Color } from 'types/colors';
import IndicatorDot from 'components/ui/IndicatorDot';
import CountryFlag from 'components/CountryFlag';
import { CountryFlagSize } from 'components/CountryFlag/CountryFlag';
import { SpriteIcon } from 'types/icons';

import css from './Thumbnail.module.scss';

export type ThumbSize = 'XXL' | 'XL' | 'L' | 'M' | 'S' | 'XS' | 'XXS';

export interface ThumbnailProps {
    size: ThumbSize;
    customSize?: number;
    shape?: 'circle' | 'square' | 'rounded';

    src?: string;
    photo?: PhotoEntity;

    badgeIcon?: SpriteIcon;
    badgeColor?: Color;

    countryFlagCode?: string;

    // Top-right indicator color
    indicatorColor?: Color;

    withOutline?: boolean;
    className?: string;

    onClick?: () => void;
}

const ICON_SIZES = {
    XXL: 30,
    XL: 25,
    L: 20,
    M: 20,
    S: 15,
    XS: 15,
    XXS: 15,
};

const FLAG_SIZES = {
    XXL: 'XL',
    XL: 'L',
    L: 'M',
    M: 'M',
    S: 'S',
    XS: 'S',
    XXS: 'S',
};

const IMAGE_SIZES = {
    XXL: 104,
    XL: 90,
    L: 58,
    M: 48,
    S: 36,
    XS: 26,
    XXS: 16,
};

// photo.thumb_small = (50x50)
// photo.thumb_medium = (100x100)
// photo.thumb_large = (180x180)
const getThumbUrl = (photo: PhotoEntity, size: ThumbSize) => {
    switch (size) {
        // 104px
        case 'XXL':
            return photo.thumb_large;

        // 90px
        case 'XL':
            return photo.thumb_medium;
        // 58px
        case 'L':
            return photo.thumb_medium;

        // 48px
        case 'M':
            return photo.thumb_small;
        // 36px
        case 'S':
            return photo.thumb_small;
        // 26px
        case 'XS':
            return photo.thumb_small;
        // 16px
        case 'XXS':
            return photo.thumb_small;
    }
};

const Thumbnail: React.FC<ThumbnailProps> = ({
    src,
    photo,
    size,
    customSize,
    badgeIcon,
    badgeColor,
    countryFlagCode,
    indicatorColor,
    withOutline = false,
    className,
    onClick,
    shape = 'circle',
}) => {
    const badgeBottomLeft =
        badgeIcon !== undefined ? (
            <Icon name={badgeIcon} width={ICON_SIZES[size]} color={badgeColor} />
        ) : (
            countryFlagCode !== undefined && (
                <CountryFlag countryCode={countryFlagCode} size={FLAG_SIZES[size] as CountryFlagSize} />
            )
        );

    const badgeTopRight = Boolean(indicatorColor) && <IndicatorDot color={indicatorColor} />;

    const sideLen = customSize || IMAGE_SIZES[size];

    const thumbUrl = src || (photo && getThumbUrl(photo, size));
    if (!thumbUrl) {
        throw new Error('Thumbnail: imgSrc or photo is required');
    }

    return (
        <div
            className={clsx(css.thumbOuter, size && css[`size-${size}`], className && className)}
            style={{ width: sideLen, height: sideLen }}
        >
            {badgeTopRight && <div className={clsx(css.badgeContainer, css.topRightBadge)}>{badgeTopRight}</div>}
            {badgeBottomLeft && <div className={clsx(css.badgeContainer, css.bottomLeftBadge)}>{badgeBottomLeft}</div>}
            <Image
                src={thumbUrl}
                alt={photo?.description}
                className={clsx(css.thumb, css[`shape-${shape}`], withOutline && css.border)}
                onClick={onClick}
                circled={false}
            />
        </div>
    );
};

export default React.memo(Thumbnail);
